import React, { useRef, useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { graphql, usePaginationFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { FireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { aquaEmailLogsQuery } from '@atlassian/jira-relay/src/__generated__/aquaEmailLogsQuery.graphql';
import type { notificationTable_spaAppsServicedeskNotificationLogs_NotificationLogsTable$key as notificationLogsFragment } from '@atlassian/jira-relay/src/__generated__/notificationTable_spaAppsServicedeskNotificationLogs_NotificationLogsTable.graphql';
import { LogsTable } from './logs-table';

const PAZE_SIZE = 30;
const NO_ERROR = 'No Error';

export const NotificationLogsTable = ({
	emailData,
	projectKey,
	outgoingMailConfigData,
	selectedTab,
}: {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	emailData: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	projectKey: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	outgoingMailConfigData: any;
	selectedTab: string;
}) => {
	const { data, loadNext } = usePaginationFragment<aquaEmailLogsQuery, notificationLogsFragment>(
		graphql`
			fragment notificationTable_spaAppsServicedeskNotificationLogs_NotificationLogsTable on Query
			@refetchable(queryName: "aquaEmailLogsQuery")
			@argumentDefinitions(count: { type: "Int", defaultValue: 20 }, cursor: { type: "String" }) {
				aquaOutgoingEmailLogs(cloudId: $cloudId) {
					GetNotificationLogs(
						projectId: $projectId
						notificationType: $notificationType
						notificationActionType: $notificationActionType
					) {
						... on AquaOutgoingEmailLog {
							logs(first: $count, after: $cursor) @connection(key: "aquaOutgoingEmail__logs") {
								edges {
									...logsTableOfTabs_logEdges
								}
								pageInfo {
									startCursor
									endCursor
									hasNextPage
									hasPreviousPage
								}
							}
						}
						... on QueryError {
							__typename
							identifier
							message
						}
					}
				}
			}
		`,
		emailData,
	);

	if (!data || !data.aquaOutgoingEmailLogs) {
		const validationError: Error = new Error('Validation Error');
		fireErrorAnalytics({
			meta: {
				id: 'LogsQueryReturnedValidationErrorInTabsLayout',
				packageName: 'jiraSpaAppsServicedeskNotificationLogs',
				teamName: 'spartans',
			},
			error: validationError,
			sendToPrivacyUnsafeSplunk: true,
		});
		throw validationError;
	}

	const responseType = data.aquaOutgoingEmailLogs?.GetNotificationLogs?.__typename;

	if (responseType === 'QueryError') {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const errorMessage: any = data.aquaOutgoingEmailLogs?.GetNotificationLogs?.message;
		const queryError: Error = new Error(errorMessage);
		fireErrorAnalytics({
			meta: {
				id: 'LogsQueryReturnedErrorInTabsLayout',
				packageName: 'jiraSpaAppsServicedeskNotificationLogs',
				teamName: 'spartans',
			},
			error: queryError,
			sendToPrivacyUnsafeSplunk: true,
		});
		throw queryError;
	}

	const hasMoreData = data.aquaOutgoingEmailLogs?.GetNotificationLogs?.logs?.pageInfo.hasNextPage;

	const allRows = data;
	const allRowEdges = allRows.aquaOutgoingEmailLogs?.GetNotificationLogs?.logs?.edges;

	const hasError = allRows.aquaOutgoingEmailLogs?.GetNotificationLogs?.identifier
		? allRows.aquaOutgoingEmailLogs?.GetNotificationLogs.message
		: NO_ERROR;

	const parentRef = useRef<HTMLDivElement>(null);
	const getRowHeight = useCallback(() => 60, []);
	const allRowsLength = allRowEdges ? allRowEdges.length : 0;

	const virtualizer = useVirtualizer({
		count: hasMoreData ? allRowsLength + 1 : allRowsLength,
		getScrollElement: () => parentRef.current,
		estimateSize: getRowHeight,
		overscan: 5,
	});

	useEffect(() => {
		// save calling `getVirtualItems()` unnecessarily
		if (!allRowEdges || !hasMoreData) {
			return;
		}

		const lastItem = virtualizer.getVirtualItems().at(-1);

		if (lastItem && lastItem.index >= allRowEdges.length - 1) {
			loadNext(PAZE_SIZE);
		}
		// Do not change the dependency to just be `virtualizer` or `virtualizer.getVirtualItems`
		// it must call the function for dependencies to be calculated correctly (and therefore render at all).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allRowEdges, hasMoreData, loadNext, virtualizer.getVirtualItems()]);

	return (
		<LogsTableWrapper tabIndex={0} ref={parentRef}>
			{hasError === NO_ERROR && (
				<>
					<FireTrackAnalytics eventName="LogsTableInTabsLayout Render" />
					<LogsTable
						virtualizer={virtualizer}
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
						allEdges={allRowEdges || ([] as any)}
						projectKey={projectKey}
						outgoingMailConfigData={outgoingMailConfigData}
						selectedTab={selectedTab}
					/>
				</>
			)}
		</LogsTableWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogsTableWrapper = styled.div({
	height: '75vh',
	width: '100%',
	overflow: 'auto',
	marginTop: token('space.200', '16px'),
});
